/**
 * InDeep-Net
 * Copyright (C) 2025
 * Authors: Fabien MAREUIL
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { applySelectionToMolstar } from './molstar_utils.js';
import { useIsDetected, useIsJobID } from './HookStateUpdate.jsx';

export const HoloResultsTable = ({ plugin }) => {
    const [results, setResults] = useState([]);
    const [hiddenRows, setHiddenRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const isDetected = useIsDetected();
    const taskIdstocked = useIsJobID();

    useEffect(() => {
      const fetchHoloResults = async () => {
        if (isDetected) {
          if (taskIdstocked !== '') {
              var taskId = taskIdstocked;
          } else {
            return;
          };
        setIsLoading(true);
        
        try {
          const response = await axios.get(`/api/parent_holo_results/${taskId}`);
          setResults(response.data.results || []); // Default to an empty array if no results
        } catch (err) {
          setError(err.response ? err.response.data.error : err.message);
        } finally {
          setIsLoading(false);
        }
      }
      
      };
      fetchHoloResults();
    }, [taskIdstocked]);
  
    

    // Function to hide a row by index
    const hideRow = (index) => {
      setHiddenRows((prev) => [...prev, index]);
    };

    const cleanSelect = (select) => {
        return select.replace(/^-s\s*/, "").replace(/^'|'$/g, ""); // Remove `-s` and surrounding single quotes
      };
  
    // Function to apply the select using Mol*Star
    const handleApplySelect = (select) => {
        applySelectionToMolstar(cleanSelect(select), plugin); // Calls the provided function to apply the selection    
        document.getElementById("analysis").scrollIntoView({behavior: "smooth"});
    };
    const [isRotating, setIsRotating] = useState(false);
    const handleReload = async () => {
      setIsRotating(!isRotating);
      if (taskIdstocked != '') {
        var jobid = taskIdstocked;
      } else { 
        return; 
      }
      setIsLoading(true);
      try {
        const response = await axios.get(`/api/parent_holo_results/${jobid}`);
        setResults(response.data.results || []); // Default to an empty array if no results
      } catch (err) {
        setError(err.response ? err.response.data.error : err.message);
      } finally {
        setIsLoading(false);
      }
      document.getElementById("holoresult").scrollIntoView({behavior: "smooth"});
      setTimeout(() => setIsRotating(false), 1000);
    };
  
    // Don't render anything if there's no taskId or results are empty
    
  
    if (isLoading) {
      return <p className="text-center text-gray-500">Loading...</p>;
    }
  
    if (error) {
      return <p className="text-center text-red-500">Error: {error}</p>;
    }
  
    return (
      <div id="holoresult" className="p-4 bg-gray-50 rounded-lg shadow-md" hidden={!isDetected}>
        <div className="flex flex-row w-full">
          <div className="flex-col w-full">
        <h2 className="text-lg font-bold text-gray-700 mb-4">InDeep<sup>Holo</sup> Results</h2>
        </div>
        <div className="flex-col">
        <button
        id="tourrefreshholo"
      className="select-none hover:bg-gray-300 text-gray-700 p-2 rounded-full border-2 border-gray-700 shadow-sm shadow-gray-500"
      onClick={handleReload}
    >
      <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className={`h-5 w-5 ${isRotating ? 'animate-spin' : ''}`}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
          />
        </svg>
    </button> 
    </div>
    </div>
        <table id="tourholoresult" className="table-auto w-full border-collapse border border-gray-300 md:text-md text-xs">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-300 p-2 md:px-4 md:py-2 text-left">PDB</th>
              <th className="border border-gray-300 p-2 md:px-4 md:py-2 text-left">Select</th>
              <th className="border border-gray-300 p-2 md:px-4 md:py-2 text-left">Value</th>
              <th className="border border-gray-300 p-2 md:px-4 md:py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
          {results.length === 0 ? (
          <tr>
            <td colSpan={4} className="text-center">
              <p>No InDeepHolo results</p>
            </td>
          </tr>
) : (results.map((result, index) => (
              // Skip rendering hidden rows
              hiddenRows.includes(index) ? null : (
                <tr key={index} className="hover:bg-gray-100">
                  <td className="border border-gray-300 p-2 md:px-4 md:py-2">{result.pdb}</td>
                  <td className="border border-gray-300 p-2 md:px-4 md:py-2 break-all">{cleanSelect(result.select)}</td>
                  <td className="border border-gray-300 p-2 md:px-4 md:py-2">{result.value}</td>
                  <td className="border border-gray-300 p-2 md:px-4 md:py-2">
                    <div className="flex flex-col md:flex-row gap-2">
                      <button
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded shadow"
                        onClick={() => hideRow(index)}
                      >
                        Hide
                      </button>
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded shadow"
                        onClick={() => handleApplySelect(result.select)}
                      >
                        Apply Select
                      </button>
                    </div>
                  </td>
                </tr>
              )
            )))}
          </tbody>
        </table>
      </div>
    );
  };
